<template>
  <div>
    <div class="actions row col-lg-12 mb-5 p-0 m-0 justify-content-between">
      <div class="actions-left">
      </div>
      <div class="actions-right row col-lg-6 row justify-content-end p-0 m-0">
        <button type="button" class="btn btn-primary" @click="addVoucher" v-if="activeTab === 'active'">{{ $t('add') }}</button>
      </div>
    </div>
    
    <!-- Tabs -->
    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'active' }" @click="switchTab('active')" href="#">
          {{ $t('active') }}
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'archived' }" @click="switchTab('archived')" href="#">
          {{ $t('archived') }}
        </a>
      </li>
    </ul>
    
    <!-- List View -->
    <div>
      <div class="search-box col-lg-12 mb-5 p-0 m-0">
        <div class="row">
          <div class="col-md-6 mb-3 mb-md-0">
            <div class="input-group">
              <input 
                type="text" 
                class="form-control" 
                @input="onSearchInput" 
                :placeholder="$t('vouchers.searchByCode')" 
                :aria-label="$t('vouchers.searchByCode')" 
                v-model="searchText"
              >
              <span class="input-group-btn">
                <button class="btn" type="button" @click="searchClicked">{{$t('search')}}</button>
              </span>
            </div>
          </div>
          <div class="col-md-6">
            <select 
              class="form-control" 
              v-model="selectedExperienceId"
              @change="onExperienceFilterChange"
            >
              <option :value="null">{{ $t('vouchers.allExperiences') }}</option>
              <option v-for="exp in experiences" :key="exp.id" :value="exp.id">
                {{ getExperienceName(exp) }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <CTableWrapper 
        ref="table" 
        hover 
        striped 
        :caption="$t('vouchers')" 
        :items="items" 
        :actions="actions" 
        :pages="pages" 
        :fields="fields" 
        clickable-rows 
        @row-clicked="rowClicked" 
        @action-clicked="actionClicked" 
        @update-requested="updateItemsRequested" 
        :sorter="{external: true}" 
        :sorterValue="sorterValue" 
        @sorter-value-changed="sorterValueChanged" 
        :loading="loading"
      >
      </CTableWrapper>
    </div>
    
    <!-- Modal de confirmação de arquivar/desarquivar -->
    <CModal
      :show.sync="showArchiveModal"
      :title="archiveModalTitle"
      color="primary"
    >
      <p>{{ archiveModalMessage }}</p>
      <template #footer>
        <CButton @click="showArchiveModal = false" color="secondary">{{ $t('cancel') }}</CButton>
        <CButton @click="confirmArchive" color="primary" :disabled="archiving">
          <CSpinner v-if="archiving" size="sm" class="mr-1"></CSpinner>
          {{ $t('confirm') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from "@/controls/PaginatedTable.vue";
import { CModal, CButton, CSpinner } from '@coreui/vue';
import router from '@/router';
import InputGetList from "@/classes/InputGetList";
import i18n from '@/helpers/i18n';
import { convertToPascalCase } from "@/helpers/utils.js";

export default {
  components: { CTableWrapper, CModal, CButton, CSpinner },
  data () {
    return {
      activeTab: 'active',
      searchText: '',
      selectedExperienceId: null,
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      searchTimeout: null,
      fields: [
        { key: 'code', label: i18n.t('vouchers.code'), _classes: 'font-weight-bold', sortable: true },
        { key: 'value', label: i18n.t('vouchers.value'), sortable: true },
        { key: 'isPercentage', label: i18n.t('vouchers.type'), sortable: true },
        { key: 'experience', label: i18n.t('vouchers.experience'), sortable: false },
        { key: 'maxUses', label: i18n.t('vouchers.maxUses'), sortable: true },
        { key: 'expirationDate', label: i18n.t('vouchers.expirationDate'), sortable: true },
      ],
      sorterValue: { column: 'code', asc: true },
      loading: false,
      showArchiveModal: false,
      voucherToArchive: null,
      archiving: false,
      pendingArchiveAction: null // 'archive' or 'unarchive'
    }
  },
  computed: {
    actions() {
      const baseActions = [
        { key: 'edit', label: i18n.t('edit') }
      ];
      
      if (this.activeTab === 'archived') {
        baseActions.push({ key: 'activate', label: i18n.t('activate') });
      } else {
        baseActions.push({ key: 'archive', label: i18n.t('archive') });
      }
      
      return baseActions;
    },
    archiveModalTitle() {
      return this.pendingArchiveAction === 'archive' ? this.$t('archive') : this.$t('activate');
    },
    archiveModalMessage() {
      const voucherCode = this.voucherToArchive && this.voucherToArchive.code ? this.voucherToArchive.code : '';
      if (this.pendingArchiveAction === 'archive') {
        return this.$t('archive') + ' ' + this.$t('vouchers.voucher') + ' "' + voucherCode + '"?';
      } else {
        return this.$t('activate') + ' ' + this.$t('vouchers.voucher') + ' "' + voucherCode + '"?';
      }
    },
    ...mapState('vouchers', ['getVouchersOutput']),
    ...mapState('vouchers', ['archiveVoucherByIdOutput']),
    ...mapState('vouchers', ['unarchiveVoucherByIdOutput']),
    ...mapState('experiences', ['getExperiencesOutput']),
    vouchersInfo: function() {
      return this.getVouchersOutput ? this.getVouchersOutput : null;
    },
    experiences: function() {
      const output = this.getExperiencesOutput;
      if (output && output.data && Array.isArray(output.data)) {
        return output.data;
      }
      return [];
    },
    items: function()
    {
      const items = this.vouchersInfo && this.vouchersInfo.data ? this.vouchersInfo.data : [];
      return items.map(item => ({
        ...item,
        value: item.isPercentage ? `${item.value}%` : `€${item.value}`,
        isPercentage: item.isPercentage ? i18n.t('vouchers.percentage') : i18n.t('vouchers.amount'),
        experience: this.getExperienceDisplayName(item),
        maxUses: item.maxUses !== null && item.maxUses !== undefined ? item.maxUses : '-',
        expirationDate: item.expirationDate ? new Date(item.expirationDate).toLocaleDateString() : '-'
      }));
    },
    pages: function()
    {
      return this.vouchersInfo ? this.vouchersInfo.totalPages : 1;
    }
  },
  mounted: function()
  {
    this.loadExperiences();
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  activated: function()
  {
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  beforeDestroy() {
    // Clear timeout when component is destroyed
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    ...mapActions('vouchers', ['getVouchers']),
    ...mapActions('vouchers', ['archiveVoucherById']),
    ...mapActions('vouchers', ['unarchiveVoucherById']),
    ...mapActions('experiences', ['getExperiences']),
    switchTab(tab) {
      if (this.activeTab !== tab) {
        this.activeTab = tab;
        this.$refs.table.reset();
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    async loadExperiences() {
      try {
        const input = new InputGetList(1000, 1, null, 1, "id");
        await this.getExperiences(input);
      } catch (error) {
        console.error('Error loading experiences:', error);
      }
    },
    getExperienceName(exp) {
      if (exp.name) return exp.name;
      if (exp.names && exp.names.pt) return exp.names.pt;
      if (exp.names && exp.names.en) return exp.names.en;
      return 'Experiência ' + exp.id;
    },
    getExperienceDisplayName(item) {
      // If experience object is loaded with the voucher
      if (item.experience) {
        // Try Name first (single string)
        if (item.experience.name && item.experience.name.trim() !== '') {
          return item.experience.name;
        }
        // Try Names dictionary (multi-language) - can be object with string keys or numeric keys
        if (item.experience.names) {
          // Handle as object (dictionary)
          if (typeof item.experience.names === 'object' && !Array.isArray(item.experience.names)) {
            // Try to get Portuguese name first (key might be "pt" or "0")
            if (item.experience.names.pt && item.experience.names.pt.trim() !== '') {
              return item.experience.names.pt;
            }
            if (item.experience.names['0'] && item.experience.names['0'].trim() !== '') {
              return item.experience.names['0'];
            }
            // Then try English (key might be "en" or "1")
            if (item.experience.names.en && item.experience.names.en.trim() !== '') {
              return item.experience.names.en;
            }
            if (item.experience.names['1'] && item.experience.names['1'].trim() !== '') {
              return item.experience.names['1'];
            }
            // Try any other key
            const keys = Object.keys(item.experience.names);
            for (const key of keys) {
              const value = item.experience.names[key];
              if (value && typeof value === 'string' && value.trim() !== '') {
                return value;
              }
            }
          }
        }
      }
      
      // If we have experienceId but no experience object, try to find it in the loaded experiences list
      if (item.experienceId && this.experiences && this.experiences.length > 0) {
        const exp = this.experiences.find(e => e.id === item.experienceId);
        if (exp) {
          return this.getExperienceName(exp);
        }
      }
      
      // Fallback
      if (!item.experienceId) {
        return i18n.t('vouchers.allExperiences');
      }
      
      // Last resort: show experience ID
      return `Experiência ${item.experienceId}`;
    },
    onSearchInput() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Set new timeout for debounced search (500ms delay)
      this.searchTimeout = setTimeout(() => {
        this.$refs.table.reset();
        this.updateItemsRequested(1, this.itemsPerPage);
      }, 500);
    },
    onExperienceFilterChange() {
      this.$refs.table.reset();
      this.updateItemsRequested(1, this.itemsPerPage);
    },
    rowClicked (item, index) {
      this.$router.push({name: "VouchersEdit", query: { id: item.id }, params : { code: item.code } });
    },
    actionClicked(item, action){
      switch(action.key)
      {
        case 'edit':
        {
          this.$router.push({name: "VouchersEdit", query: { id: item.id }, params : { code: item.code } });
          break;
        }
        case 'archive':
        {
          this.voucherToArchive = item;
          this.pendingArchiveAction = 'archive';
          this.showArchiveModal = true;
          break;
        }
        case 'activate':
        {
          this.voucherToArchive = item;
          this.pendingArchiveAction = 'unarchive';
          this.showArchiveModal = true;
          break;
        }
        default:
        {
          throw "The provided action is not supported!"; 
        }
      }
    },
    updateItemsRequested(currentPage, itemsPerPage)
    {
      this.loading = true;
      this.itemsPerPage = itemsPerPage;

      var input = new InputGetList(itemsPerPage, currentPage, this.searchText, this.sorterValue.asc ? 1 : 2, convertToPascalCase(this.sorterValue.column));

      const isArchived = this.activeTab === 'archived';
      this.getVouchers({ input, isActive: null, experienceId: this.selectedExperienceId, isArchived: isArchived }); 
    },
    addVoucher () 
    {
      router.push({ name: 'VouchersAdd'});
    },
    searchClicked()
    {
      this.$refs.table.reset();
      this.updateItemsRequested(1, this.itemsPerPage);
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    },
    async confirmArchive() {
      if (!this.voucherToArchive || !this.pendingArchiveAction) return;
      
      const action = this.pendingArchiveAction; // Store before clearing
      
      try {
        this.archiving = true;
        
        if (action === 'archive') {
          await this.archiveVoucherById(this.voucherToArchive.id);
        } else {
          await this.unarchiveVoucherById(this.voucherToArchive.id);
        }
        
        this.showArchiveModal = false;
        this.voucherToArchive = null;
        this.pendingArchiveAction = null;
        this.archiving = false;
        
        this.updateItemsRequested(1, this.itemsPerPage);
        
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: action === 'archive' 
            ? this.$t('vouchers.archiveSuccess') 
            : this.$t('vouchers.unarchiveSuccess')
        });
      } catch (error) {
        console.error('Error archiving/unarchiving voucher:', error);
        this.archiving = false;
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: action === 'archive'
            ? this.$t('vouchers.archiveError')
            : this.$t('vouchers.unarchiveError')
        });
      }
    }
  },
  watch:
  {
    sorterValue:
    {
      handler: function(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true
    },
    getVouchersOutput: function(val)
    {
      if(val != null)
      {
        this.loading = false;
      }
    },
    archiveVoucherByIdOutput: function(val) {
      if (val != null) {
        this.archiving = false;
      }
    },
    unarchiveVoucherByIdOutput: function(val) {
      if (val != null) {
        this.archiving = false;
      }
    }
  }
}
</script>

<style scoped>
.actions-left {
  display: flex;
  align-items: center;
}

.btn-group .btn {
  margin-right: 0;
}

.search-box .input-group-btn .btn {
  background-color: var(--primary);
  color: var(--secondary);
}
</style>

